import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import toast from '../../utils/toast';
import {
  palette,
  radius,
  shadow,
  spacing,
  typography,
  zIndex,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { useDialogFocus } from '../hooks/use-dialog-focus';
import { ModalPortal } from '../modal/modal-portal';
import { Presence, m, scrimMotion, useOverlayMotion } from '../../motion';
import { AppButton } from '../button/app-button';
import { getUnit, listUnits } from '../../api/hierarchy-client';
import { UnitOccupant } from '../../interfaces/hierarchy-interfaces';
import {
  ReturnResolution,
  getUnitAllowance,
  previewUnitReturn,
  returnUnits,
} from '../../api/units-client';
import { compedRefusal } from '../../api/plan-client';
import { formatDate } from '../../function-utils/locale';

// Minor units to a readable amount — same shape as the billing page's helper,
// local so this dialog stays importable on its own.
const money = (minor: number, currency: string): string =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format((minor || 0) / 100);

/* Returning units — the reduction the pool never had. The customer names the
 * EXACT units they are giving back; the system never chooses for them. The
 * named units are ARCHIVED (soft — same id, same building, same history,
 * restorable later) and the subscription quantity drops by the count.
 *
 * THE POOL HAS TWO HALVES AND THIS DIALOG USED TO SHOW ONE. A purchased unit
 * only becomes a row once it is ALLOCATED to a building; signup allocates
 * none. So an owner who had bought 150 units and allocated zero opened this
 * dialog and read "There are no billable units to return." — true about rows,
 * a lie about the bill, and every one of those 150 units was being invoiced.
 * Unallocated capacity (`units_remaining`) is now a returnable line of its
 * own: a count, not a picker, because there is nothing to name. Both halves
 * travel in one request and land as one Stripe decrement.
 *
 * OCCUPIED units can be returned too (SPEC_RETURN_WITH_MOVEOUT), but every
 * resident needs an EXPLICIT per-person destination first — Move out, or Move
 * to another unit — chosen here, applied server-side inside the same locked
 * transaction as the archive. The system never picks where a person goes, and
 * Continue stays disabled until every occupant of every selected unit is
 * resolved.
 *
 * Two steps on purpose (Phase-21 clarity): first pick the units and resolve
 * the people, then read a plain-words statement of the consequence — the new
 * plan total, how many residents move out or move — behind a
 * destructive-variant confirm. */

const Scrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.md};
  background: ${palette.overlay};

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

const Card = m.div`
  width: 100%;
  max-width: 460px;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  padding: ${spacing.lg};

  ${down('sm')} {
    max-width: none;
    max-height: calc(100vh - ${spacing.sm});
    max-height: calc(100dvh - ${spacing.sm});
    overflow-y: auto;
    border-radius: ${radius.lg} ${radius.lg} 0 0;
    padding: ${spacing.md} ${spacing.sm};
  }
`;

const Title = styled.h2`
  margin: 0 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: -0.014em;
  color: ${palette.ink};
`;

const Message = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const List = styled.div`
  max-height: 260px;
  margin-bottom: ${spacing.sm};
  padding: ${spacing.xs};
  overflow-y: auto;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
`;

const Group = styled.div`
  margin-bottom: ${spacing.sm};
`;

const GroupName = styled.div`
  margin-bottom: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slate};
`;

const Row = styled.label<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${({ disabled }) => (disabled ? palette.slateLight : palette.ink)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background: ${({ disabled }) =>
      disabled ? 'transparent' : palette.irisTintSoft};
  }
`;

const Hint = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const Warning = styled.div`
  margin: 0 0 ${spacing.md};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.alertSoft};
  color: ${palette.inkSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};

  & b {
    color: ${palette.ink};
  }
`;

const Refusal = styled.p`
  margin: 0 0 ${spacing.md};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.tealWashSoft};
  color: ${palette.inkSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${spacing.xs};
`;

interface ReturnableUnit {
  id: number;
  unit_number: string;
  name: string | null;
  buildingName: string;
  occupantCount: number;
}

/* One occupant's destination row. Radios, not buttons: the choice is
 * exclusive per person and must read as such to a screen reader. */
const OccupantRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  margin: ${spacing.half} 0 ${spacing.half} ${spacing.lg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.inkSoft};
`;

const OccupantName = styled.span`
  min-width: 120px;
  font-weight: ${typography.weight.medium};
  color: ${palette.ink};
`;

const ResChoice = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const TargetSelect = styled.select`
  height: 32px;
  padding: 0 ${spacing.half};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

const OccupantChip = styled.span`
  margin-left: auto;
  font-size: ${typography.size.xs}px;
  color: ${palette.slateLight};
`;

/* The unallocated line. Not a checkbox: there are no rows to tick, only a
 * number of paid-for slots, so the control is the number itself. */
const SpareRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.half} ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
`;

const SpareInput = styled.input`
  width: 72px;
  height: 32px;
  padding: 0 ${spacing.half};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  /* 16px on iOS or Safari zooms the whole dialog on focus. */
  font-size: ${typography.size.sm}px;

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

const SpareNote = styled.span`
  flex-basis: 100%;
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

export interface ReturnUnitsDialogProps {
  open: boolean;
  // The org's current bought total — the plan cannot drop below 1, so at most
  // unitsAllowed - 1 can be selected.
  unitsAllowed: number;
  onDismiss: () => void;
  onReturned: (result: { returned: number; units_allowed: number }) => void;
}

export const ReturnUnitsDialog: React.FC<ReturnUnitsDialogProps> = ({
  open,
  unitsAllowed,
  onDismiss,
  onReturned,
}) => {
  const [units, setUnits] = useState<ReturnableUnit[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  // Paid for, never allocated to a building — the allowance's own
  // `units_remaining`. `spareAvailable` is what exists; `spare` is how many of
  // them this return gives back.
  const [spareAvailable, setSpareAvailable] = useState(0);
  const [spare, setSpare] = useState(0);
  const [step, setStep] = useState<'choose' | 'confirm'>('choose');
  const [refusal, setRefusal] = useState<string | null>(null);
  // Occupants per occupied unit, fetched lazily on selection ('loading' /
  // 'error' are states the gating below treats as unresolved).
  const [occupants, setOccupants] = useState<
    Record<number, UnitOccupant[] | 'loading' | 'error'>
  >({});
  // Every occupant's destination, keyed by occupancy id. The system never
  // chooses: an occupant without an entry blocks Continue.
  const [resolutions, setResolutions] = useState<
    Record<string, { action: 'move_out' | 'transfer'; to_unit_id?: number }>
  >({});
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  // The quoted credit for the CURRENT selection, from Stripe's own proration
  // preview. null = no quote (still loading, or the preview failed) — the
  // quote is a courtesy and its absence must never block a return.
  const [credit, setCredit] = useState<{
    cents: number;
    currency: string;
    // Where the credit lands: the next invoice — on an annual plan, the
    // renewal. Named so an annual customer is not left expecting it next month.
    interval?: string | null;
    appliesAt?: string | null;
  } | null>(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const creditSeqRef = useRef(0);

  // Quoted when the reader reaches the consequence step — that is where the
  // number is read, and quoting every checkbox tick would spam Stripe.
  useEffect(() => {
    if (step !== 'confirm' || selected.length + spare === 0) {
      setCredit(null);

      return undefined;
    }
    const seq = creditSeqRef.current + 1;
    creditSeqRef.current = seq;
    setCreditLoading(true);
    previewUnitReturn(selected, spare)
      .then(q => {
        if (creditSeqRef.current !== seq) return;
        setCredit({
          cents: q.credit_cents,
          currency: q.currency,
          interval: q.interval ?? null,
          appliesAt: q.credit_applies_at ?? null,
        });
      })
      .catch(() => {
        if (creditSeqRef.current !== seq) return;
        setCredit(null);
      })
      .finally(() => {
        if (creditSeqRef.current === seq) setCreditLoading(false);
      });

    return undefined;
  }, [step, selected, spare]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setUnits(null);
    setLoadError(false);
    setSelected([]);
    setSpareAvailable(0);
    setSpare(0);
    setStep('choose');
    setRefusal(null);
    setOccupants({});
    setResolutions({});

    let alive = true;
    // The allowance is the source of truth for WHICH buildings hold allocated
    // units; each one's full inventory is then fetched — occupied units are
    // returnable too now, their residents resolved inline below. Sequenced
    // through the same endpoints every units screen uses — nothing derives.
    //
    // It also carries the OTHER half of the pool: `units_remaining` is what
    // has been paid for and never allocated. That number has no rows behind
    // it, which is exactly why it was invisible here before.
    getUnitAllowance()
      .then(a => {
        if (alive) {
          setSpareAvailable(Math.max(0, a.units_remaining ?? 0));
        }

        return Promise.all(
          a.buildings
            .filter(b => b.allocated > 0)
            .map(b =>
              listUnits(b.id, {}).then(list =>
                list
                  // Only billable rows can hand a slot back: archived ones
                  // already did, and placeholders were never billed.
                  .filter(
                    u =>
                      (u.status === 'active' || u.status === 'offline') &&
                      u.kind !== 'unassigned'
                  )
                  .map(u => ({
                    id: u.id,
                    unit_number: u.unit_number,
                    name: u.name ?? null,
                    buildingName: b.name,
                    occupantCount: u.occupant_count,
                  }))
              )
            )
        );
      })
      .then(lists => {
        if (alive) {
          setUnits(lists.flat());
        }
      })
      .catch(() => {
        if (alive) {
          setLoadError(true);
        }
      });

    return () => {
      alive = false;
    };
  }, [open]);

  // Selecting an occupied unit fetches its residents (names decrypt
  // server-side); deselecting drops their resolutions so a stale choice can
  // never ride along on a later submit.
  const toggleUnit = (u: ReturnableUnit) => {
    setSelected(prev => {
      const picking = !prev.includes(u.id);
      if (!picking) {
        const list = occupants[u.id];
        if (Array.isArray(list)) {
          setResolutions(current => {
            const next = { ...current };
            for (const o of list) delete next[o.id];

            return next;
          });
        }

        return prev.filter(v => v !== u.id);
      }
      if (u.occupantCount > 0 && occupants[u.id] === undefined) {
        setOccupants(current => ({ ...current, [u.id]: 'loading' }));
        getUnit(u.id)
          .then(detail =>
            setOccupants(current => ({
              ...current,
              [u.id]: detail.occupants ?? [],
            }))
          )
          .catch(() =>
            setOccupants(current => ({ ...current, [u.id]: 'error' }))
          );
      }

      return [...prev, u.id];
    });
  };

  const handleDismiss = () => {
    if (!busyRef.current) {
      onDismiss();
    }
  };
  const dialogRef = useDialogFocus(open, handleDismiss);
  const cardMotion = useOverlayMotion('modal');

  // The plan cannot drop below 1 unit (the server refuses with
  // RETURN_BELOW_MINIMUM); the last unit's exit is a cancellation instead.
  // The floor applies to the TOTAL: named units and unallocated slots come out
  // of the same subscription quantity.
  const maxReturnable = Math.max(0, unitsAllowed - 1);
  const totalReturning = selected.length + spare;
  const atCap = totalReturning >= maxReturnable;
  // How many unallocated slots this return may still take: never more than
  // exist, never more than the floor leaves once the named units are counted.
  const spareCeiling = Math.max(
    0,
    Math.min(spareAvailable, maxReturnable - selected.length)
  );

  // Picking more named units can push the unallocated count past the floor, so
  // it is clamped rather than left to be refused server-side.
  useEffect(() => {
    setSpare(current => (current > spareCeiling ? spareCeiling : current));
  }, [spareCeiling]);

  const groups = useMemo(() => {
    const byBuilding = new Map<string, ReturnableUnit[]>();
    for (const u of units ?? []) {
      const list = byBuilding.get(u.buildingName) ?? [];
      list.push(u);
      byBuilding.set(u.buildingName, list);
    }

    return [...byBuilding.entries()];
  }, [units]);

  // Transfer targets: vacant billable units NOT themselves being returned.
  const targets = useMemo(
    () =>
      (units ?? []).filter(
        u => u.occupantCount === 0 && !selected.includes(u.id)
      ),
    [units, selected]
  );

  // Continue is gated on every resident of every selected unit having a
  // complete destination — resolved here so the disabled state can say why.
  const pendingPeople = useMemo(() => {
    let unresolved = 0;
    let loading = false;
    for (const id of selected) {
      const u = (units ?? []).find(x => x.id === id);
      if (!u || u.occupantCount === 0) continue;
      const list = occupants[id];
      if (!Array.isArray(list)) {
        loading = true;
        continue;
      }
      for (const o of list) {
        const r = resolutions[o.id];
        if (!r || (r.action === 'transfer' && !r.to_unit_id)) unresolved += 1;
      }
    }

    return { unresolved, loading };
  }, [selected, units, occupants, resolutions]);

  const peopleCounts = useMemo(() => {
    let movedOut = 0;
    let transferred = 0;
    for (const id of selected) {
      const list = occupants[id];
      if (!Array.isArray(list)) continue;
      for (const o of list) {
        const r = resolutions[o.id];
        if (r?.action === 'move_out') movedOut += 1;
        if (r?.action === 'transfer' && r.to_unit_id) transferred += 1;
      }
    }

    return { movedOut, transferred };
  }, [selected, occupants, resolutions]);

  const submit = async () => {
    if (busyRef.current || totalReturning === 0) {
      return;
    }

    busyRef.current = true;
    setBusy(true);
    setRefusal(null);
    try {
      const body: ReturnResolution[] = [];
      for (const id of selected) {
        const list = occupants[id];
        if (!Array.isArray(list)) continue;
        for (const o of list) {
          const r = resolutions[o.id];
          if (r) {
            body.push({
              occupancy_id: o.id,
              action: r.action,
              ...(r.action === 'transfer' ? { to_unit_id: r.to_unit_id } : {}),
            });
          }
        }
      }
      const result = await returnUnits(selected, body, spare);
      const people =
        (result.moved_out ?? 0) + (result.transferred ?? 0) > 0
          ? ` — ${result.moved_out ?? 0} moved out, ${
              result.transferred ?? 0
            } moved to another unit`
          : '';
      toast.success(
        `Returned ${result.returned} unit${
          result.returned === 1 ? '' : 's'
        } — your plan is now ${result.units_allowed} units${people}`
      );
      onReturned(result);
    } catch (error) {
      const data =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data
          : undefined;
      setRefusal(
        compedRefusal(error) ||
          data?.message ||
          'Could not return the units. Please try again.'
      );
      setStep('choose');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const newTotal = unitsAllowed - totalReturning;

  return (
    <Presence>
      {open ? (
    <ModalPortal key="return-units-dialog">
    <Scrim {...scrimMotion} onClick={handleDismiss}>
      <Card
        {...cardMotion}
        ref={dialogRef}
        tabIndex={-1}
        role="alertdialog"
        aria-modal="true"
        aria-label="Return units"
        onClick={e => e.stopPropagation()}
      >
        <Title>Return units</Title>
        <Message>
          Pick the units to give back — by name where they are already in a
          building, or by count where you have paid for them but not assigned
          them yet. Your plan drops by that many units now, and the unused
          time is credited against your next invoice — on a yearly plan, that
          is your annual renewal.
        </Message>

        {refusal && <Refusal role="alert">{refusal}</Refusal>}

        {step === 'choose' && (
          <>
            {loadError && (
              <Refusal role="alert">
                We could not load your units. Close this and try again.
              </Refusal>
            )}
            {units === null && !loadError && <Hint>Loading your units…</Hint>}
            {/* Only when BOTH halves of the pool are empty. This used to fire
                whenever there were no allocated rows, which told an owner who
                had bought 150 units and allocated none that they had nothing
                to return while being invoiced for all 150. */}
            {units && units.length === 0 && spareAvailable === 0 && (
              <Hint>There are no billable units to return.</Hint>
            )}
            {units && (units.length > 0 || spareAvailable > 0) && (
              <List role="group" aria-label="Units to return">
                {spareAvailable > 0 && (
                  <Group>
                    <GroupName>Not assigned to a building</GroupName>
                    <SpareRow>
                      <label htmlFor="return-unallocated">
                        Return
                      </label>
                      <SpareInput
                        id="return-unallocated"
                        type="number"
                        min={0}
                        max={spareCeiling}
                        step={1}
                        value={spare}
                        disabled={busy}
                        onChange={e => {
                          const n = Math.floor(Number(e.target.value));
                          setSpare(
                            Number.isFinite(n)
                              ? Math.max(0, Math.min(n, spareCeiling))
                              : 0
                          );
                        }}
                        aria-label="Unallocated units to return"
                      />
                      of {spareAvailable} unallocated unit
                      {spareAvailable === 1 ? '' : 's'}
                      <SpareNote>
                        Paid for, not yet placed in a building. Nothing is
                        archived and no resident is affected — the plan simply
                        covers fewer units.
                      </SpareNote>
                    </SpareRow>
                  </Group>
                )}
                {groups.map(([building, list]) => (
                  <Group key={building}>
                    <GroupName>{building}</GroupName>
                    {list.map(u => {
                      const capped = atCap && !selected.includes(u.id);
                      const disabled = capped || busy;
                      const picked = selected.includes(u.id);
                      const occ = occupants[u.id];

                      return (
                        <React.Fragment key={u.id}>
                          <Row disabled={disabled}>
                            <input
                              type="checkbox"
                              checked={picked}
                              disabled={disabled}
                              onChange={() => toggleUnit(u)}
                              aria-label={`Return unit ${u.unit_number} in ${u.buildingName}`}
                            />
                            Unit {u.unit_number}
                            {u.name ? ` — ${u.name}` : ''}
                            {u.occupantCount > 0 && (
                              <OccupantChip>
                                {u.occupantCount} resident
                                {u.occupantCount === 1 ? '' : 's'}
                              </OccupantChip>
                            )}
                          </Row>
                          {picked && u.occupantCount > 0 && occ === 'loading' && (
                            <OccupantRow>Loading residents…</OccupantRow>
                          )}
                          {picked && u.occupantCount > 0 && occ === 'error' && (
                            <OccupantRow role="alert">
                              Could not load this unit&apos;s residents —
                              deselect and try again.
                            </OccupantRow>
                          )}
                          {picked &&
                            Array.isArray(occ) &&
                            occ.map(o => {
                              const r = resolutions[o.id];
                              const personName =
                                `${o.first_name ?? ''} ${o.last_name ?? ''}`.trim() ||
                                'Resident';

                              return (
                                <OccupantRow key={o.id}>
                                  <OccupantName>{personName}</OccupantName>
                                  <ResChoice>
                                    <input
                                      type="radio"
                                      name={`res-${o.id}`}
                                      checked={r?.action === 'move_out'}
                                      disabled={busy}
                                      onChange={() =>
                                        setResolutions(current => ({
                                          ...current,
                                          [o.id]: { action: 'move_out' },
                                        }))
                                      }
                                      aria-label={`Move ${personName} out`}
                                    />
                                    Move out
                                  </ResChoice>
                                  <ResChoice>
                                    <input
                                      type="radio"
                                      name={`res-${o.id}`}
                                      checked={r?.action === 'transfer'}
                                      disabled={busy}
                                      onChange={() =>
                                        setResolutions(current => ({
                                          ...current,
                                          [o.id]: { action: 'transfer' },
                                        }))
                                      }
                                      aria-label={`Move ${personName} to another unit`}
                                    />
                                    Move to
                                  </ResChoice>
                                  <TargetSelect
                                    value={r?.to_unit_id ?? ''}
                                    disabled={busy || r?.action !== 'transfer'}
                                    onChange={e =>
                                      setResolutions(current => ({
                                        ...current,
                                        [o.id]: {
                                          action: 'transfer',
                                          to_unit_id:
                                            Number(e.target.value) || undefined,
                                        },
                                      }))
                                    }
                                    aria-label={`Destination unit for ${personName}`}
                                  >
                                    <option value="">choose a unit…</option>
                                    {targets.map(tu => (
                                      <option key={tu.id} value={tu.id}>
                                        Unit {tu.unit_number} — {tu.buildingName}
                                      </option>
                                    ))}
                                  </TargetSelect>
                                </OccupantRow>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                  </Group>
                ))}
              </List>
            )}
            <Hint>
              A unit with residents can be returned once every resident has a
              destination — moved out, or moved to another unit; nobody is
              moved automatically. Your plan cannot drop below 1 unit; to give
              up everything, cancel the subscription instead.
            </Hint>
            <Actions>
              <AppButton variant="ghost" disabled={busy} onClick={handleDismiss}>
                Keep everything
              </AppButton>
              <AppButton
                variant="primary"
                disabled={
                  busy ||
                  totalReturning === 0 ||
                  pendingPeople.loading ||
                  pendingPeople.unresolved > 0
                }
                title={
                  pendingPeople.unresolved > 0
                    ? `${pendingPeople.unresolved} resident${
                        pendingPeople.unresolved === 1 ? '' : 's'
                      } still need${
                        pendingPeople.unresolved === 1 ? 's' : ''
                      } a destination`
                    : undefined
                }
                onClick={() => setStep('confirm')}
              >
                Continue
              </AppButton>
            </Actions>
          </>
        )}

        {step === 'confirm' && (
          <>
            <Warning role="alert">
              You are returning <b>{totalReturning}</b> unit
              {totalReturning === 1 ? '' : 's'}
              {spare > 0 && selected.length > 0 ? (
                <>
                  {' '}
                  — <b>{selected.length}</b> from your buildings and{' '}
                  <b>{spare}</b> not yet assigned to one
                </>
              ) : null}
              {spare > 0 && selected.length === 0 ? (
                <> that were never assigned to a building</>
              ) : null}
              . Your plan goes from <b>{unitsAllowed}</b> to <b>{newTotal}</b>{' '}
              units and your price drops accordingly.{' '}
              {creditLoading ? (
                <>Fetching the exact credit… </>
              ) : credit && credit.cents > 0 ? (
                <>
                  About <b>{money(credit.cents, credit.currency)}</b> of unused
                  time is credited on your next invoice
                  {credit.interval === 'year' && credit.appliesAt
                    ? ` — your annual renewal on ${formatDate(credit.appliesAt)}`
                    : ''}
                  . It is a credit, not a refund to your card.{' '}
                </>
              ) : null}
              {peopleCounts.movedOut + peopleCounts.transferred > 0 && (
                <>
                  <b>{peopleCounts.movedOut}</b> resident
                  {peopleCounts.movedOut === 1 ? '' : 's'} will be moved out
                  and <b>{peopleCounts.transferred}</b> moved to another unit —
                  their records and history are kept.{' '}
                </>
              )}
              {/* Only true of NAMED units. Unallocated capacity has no row to
                  take offline and nothing to restore, so saying this about it
                  would be the same kind of lie the empty state used to tell. */}
              {selected.length > 0 &&
                'The units you named go offline — they stop appearing at the ' +
                  'desk, in reports and to residents — but nothing is deleted: ' +
                  'each keeps its number, its building and its history, and can ' +
                  'be restored later after buying units again.'}
            </Warning>
            <Actions>
              <AppButton
                variant="ghost"
                disabled={busy}
                onClick={() => setStep('choose')}
              >
                Back
              </AppButton>
              <AppButton variant="destructive" disabled={busy} onClick={submit}>
                {busy
                  ? 'Returning…'
                  : `Return ${totalReturning} unit${
                      totalReturning === 1 ? '' : 's'
                    }`}
              </AppButton>
            </Actions>
          </>
        )}
      </Card>
    </Scrim>
    </ModalPortal>
      ) : null}
    </Presence>
  );
};
